import { Link } from "react-router-dom";
import { Shell } from "../components/Shell";

/**
 * Guía para el funcionario supervisor de una alcaldía.
 * Cubre la operación táctica del equipo: monitorear, reasignar,
 * cuidar el cumplimiento del SLA legal y reportar al alcalde.
 */
export function DocsSupervisorPage() {
  return (
    <Shell>
      <section className="page" style={{ maxWidth: 880 }}>
        <div className="eyebrow">
          <Link to="/docs">← Documentación</Link> · Guía del supervisor
        </div>

        <h1 style={{ margin: "8px 0 12px", fontSize: 32 }}>
          Guía del supervisor
        </h1>

        <div className="callout">
          <strong>Tiempo de lectura:</strong> 25-35 min. Léela una vez al
          asumir el rol y vuelve a la sección 10 cada mes para los reportes.
        </div>

        {/* ---- 1. Rol ---- */}
        <Section title="1. Tu rol como supervisor">
          <p>
            Eres el <strong>responsable táctico</strong> de que la atención al
            ciudadano funcione. No atiendes el día a día (eso lo hacen los
            operadores), pero sí garantizas que:
          </p>
          <Ul
            items={[
              <>
                <strong>Nadie quede sin atender:</strong> la cola de chat se
                vacía rápido y los PQRSDFs se reparten bien
              </>,
              <>
                <strong>El SLA legal se cumple</strong> (Ley 1755/2015): cero
                vencidos
              </>,
              <>
                <strong>El equipo está calibrado:</strong> calidad de respuesta
                pareja, tono institucional, sin errores normativos
              </>,
              <>
                <strong>La dirección sabe qué pasa:</strong> informes
                periódicos al alcalde / secretario general
              </>,
            ]}
          />
          <p>
            Tienes acceso a casi todo lo del admin del tenant, pero{" "}
            <strong>no</strong> creas funcionarios ni cambias configuración
            (canales, IA, branding) — eso es del <code>admin_tenant</code>.
          </p>
        </Section>

        {/* ---- 2. Dashboard ---- */}
        <Section title="2. Dashboard de equipo">
          <p>
            En <Kbd>Panel</Kbd> (vista por defecto al entrar) tienes la foto
            global del tenant:
          </p>
          <Table
            head={["KPI", "Para qué te sirve"]}
            rows={[
              [
                "Total radicados",
                "Volumen agregado del tenant. Crecimiento = más adopción.",
              ],
              [
                "Vencidos",
                "El único KPI que NO puede estar en rojo. Si lo está, intervén ya.",
              ],
              [
                "Próximos a vencer (3 días)",
                "Tu radar — distribúyelos hoy para que mañana no se vuelvan rojos.",
              ],
              [
                "Respondidos últimos 7 días",
                "Throughput del equipo. Si baja, algo está pasando (gente enferma, picos, etc.).",
              ],
              [
                "Tiempo promedio de respuesta",
                "Calidad operativa. Compáralo entre semanas.",
              ],
              [
                "Distribución por tipo / canal / estado",
                "Te dice qué área está cargada (ej. mucha queja → revisar ese servicio público)",
              ],
            ]}
          />
          <p>
            En <Kbd>Mi bandeja</Kbd> usa el filtro <Kbd>Todos</Kbd> en lugar de{" "}
            <strong>Mis radicados</strong> para ver toda la operación del
            tenant.
          </p>
        </Section>

        {/* ---- 3. Bandeja Cerradas ---- */}
        <Section title="3. Chat — bandeja Cerradas y filtros">
          <p>
            En <Kbd>Chat ciudadano</Kbd> tienes la pestaña{" "}
            <Kbd>Cerradas</Kbd> que solo tú y el admin usan a diario. Sirve
            para auditar conversaciones pasadas.
          </p>
          <Ol>
            <li>
              Click <Kbd>Cerradas</Kbd> → aparecen los filtros bajo la lista
            </li>
            <li>
              Filtros disponibles:
              <ul>
                <li>
                  <strong>Buscar</strong> (texto libre por nombre, email,
                  contenido)
                </li>
                <li>
                  <strong>Operador</strong> — ver todo lo que cerró una persona
                  específica
                </li>
                <li>
                  <strong>Outcome</strong>:
                  <ul>
                    <li>
                      <code>resuelta_bot</code> — el bot lo arregló sin operador
                    </li>
                    <li>
                      <code>resuelta_operador</code> — atendido y cerrado por
                      operador
                    </li>
                    <li>
                      <code>escalada_pqrsdf</code> — se generó radicado formal
                    </li>
                    <li>
                      <code>cerrada</code> — cerrada sin clasificar (ej. el
                      ciudadano abandonó)
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Rango de fechas</strong> — desde / hasta
                </li>
              </ul>
            </li>
            <li>
              Click en una conversación → ves el transcript completo, encuesta
              respondida (si la hay), tiempos
            </li>
          </Ol>
          <div className="callout tip">
            💡 <strong>Tip semanal:</strong> filtra por la semana pasada y
            outcome <code>escalada_pqrsdf</code> — ahí ves las consultas que
            tu equipo NO pudo resolver. Si hay un patrón repetido, vale la
            pena crear un artículo en el centro de ayuda o capacitar al
            equipo.
          </div>
        </Section>

        {/* ---- 4. Reasignar ---- */}
        <Section title="4. Reasignar PQRSDFs entre operadores">
          <p>
            Hay dos formas de reasignar trabajo:
          </p>
          <h4>Caso A — Trasladar a otra dependencia</h4>
          <Ol>
            <li>
              Entra al radicado desde la bandeja
            </li>
            <li>
              Click <Kbd>Trasladar</Kbd> en la barra de acciones del radicado
            </li>
            <li>
              Elige la dependencia destino (Hacienda, Movilidad, etc.) — el
              radicado queda en cola de esa dependencia. Los operadores de esa
              área lo verán.
            </li>
            <li>
              Escribe la razón del traslado (queda en la auditoría del
              expediente)
            </li>
          </Ol>

          <h4 style={{ marginTop: 18 }}>Caso B — Reasignar a otro operador específico</h4>
          <Ol>
            <li>
              Entra al radicado
            </li>
            <li>
              En el panel de asignación, elige el nuevo responsable
            </li>
            <li>
              El sistema notifica al operador entrante. El plazo legal NO se
              reinicia con la reasignación.
            </li>
          </Ol>
          <div className="callout warn">
            <strong>Cuida los plazos:</strong> reasignar a 2 días del
            vencimiento es injusto para el operador entrante. Si el plazo está
            apretado, ayuda tú directamente a redactar la respuesta antes de
            reasignar.
          </div>
        </Section>

        {/* ---- 5. Alertas SLA ---- */}
        <Section title="5. Alertas de SLA en rojo y cómo actuar">
          <p>
            El KPI <strong>Vencidos</strong> es el que no puede estar nunca en
            rojo. Cuando lo veas:
          </p>
          <Ol>
            <li>
              <Kbd>Mi bandeja</Kbd> → chip <Kbd>Próximos / vencidos</Kbd> →
              identifica los radicados rojos
            </li>
            <li>
              Para cada uno:
              <ul>
                <li>
                  ¿Está asignado? → contacta al operador asignado, pídele que
                  responda hoy
                </li>
                <li>
                  ¿Está sin asignar? → asígnalo ya, o respóndelo tú mismo si
                  está al alcance
                </li>
                <li>
                  ¿Es de otra dependencia? → escala al admin del tenant o al
                  jefe del área
                </li>
              </ul>
            </li>
            <li>
              Si un radicado ya venció, la respuesta sigue siendo obligatoria —
              respóndelo igual, incluyendo una <strong>disculpa</strong> al
              ciudadano por el atraso
            </li>
            <li>
              Documenta la causa (faltó personal, error de asignación, etc.)
              para discutirla con el admin_tenant en el siguiente comité
            </li>
          </Ol>
          <div className="callout warn">
            <strong>Recuerda:</strong> el ciudadano puede demandar a la
            alcaldía por silencio administrativo si no se le responde un
            PQRSDF en el plazo de ley. Vencidos = riesgo jurídico real.
          </div>
        </Section>

        {/* ---- 6. Métricas por funcionario ---- */}
        <Section title="6. Métricas por funcionario">
          <p>
            En <Kbd>Métricas e informes</Kbd> → vista{" "}
            <Kbd>Por funcionario</Kbd> tienes para cada operador:
          </p>
          <Table
            head={["Métrica", "Lectura recomendada"]}
            rows={[
              [
                "Volumen atendido",
                "Compara entre operadores con cargas similares — desbalances grandes = redistribuir",
              ],
              [
                "Tiempo promedio de primera respuesta",
                "Calidad de servicio. > 5 min en chat es señal de saturación",
              ],
              [
                "% respondidos en plazo",
                "Debería estar en 100%. Cualquier % menor = alerta",
              ],
              [
                "NPS / rating de encuestas",
                "Calidad percibida. < 3.5 promedio amerita revisión de tono y plantillas",
              ],
              [
                "Tasa de escalado a PQRSDF",
                "Muy alta = no sabe resolver en chat. Muy baja = puede estar prometiendo lo que no debe",
              ],
              [
                "% resueltas (vs abandonadas)",
                "Buena referencia de efectividad",
              ],
            ]}
          />
          <div className="callout tip">
            💡 No uses estas métricas como castigo. Úsalas como conversación:
            "veo que tu tiempo de respuesta subió esta semana, ¿qué está
            pasando?". Probablemente sea contexto (más volumen, casos
            difíciles, etc.).
          </div>
        </Section>

        {/* ---- 7. Encuestas ---- */}
        <Section title="7. Encuestas — leer NPS y comentarios">
          <p>
            Cada chat cerrado dispara una encuesta automática. Para revisarlas:
          </p>
          <Ol>
            <li>
              <Kbd>Chat ciudadano</Kbd> → pestaña <Kbd>Cerradas</Kbd>
            </li>
            <li>
              Cada conversación cerrada muestra un banner con:
              <ul>
                <li>
                  <strong>Calificación</strong> (1-5 estrellas)
                </li>
                <li>
                  <strong>¿Resolvió?</strong> sí / parcial / no
                </li>
                <li>
                  <strong>Comentario libre</strong> del ciudadano
                </li>
              </ul>
            </li>
            <li>
              Banner verde = ≥ 4 estrellas · banner rojo = ≤ 2 estrellas
            </li>
          </Ol>
          <p>
            <strong>Rutina semanal recomendada:</strong> revisa todas las
            calificaciones de 1-2 estrellas de la semana. Lee el comentario y
            la conversación completa. Patrones que vale la pena identificar:
          </p>
          <Ul
            items={[
              "El ciudadano no entendió la respuesta (revisar plantillas / tono)",
              "El operador respondió rápido pero sin resolver (faltó info en KB)",
              "El bot dio info incorrecta antes de pasar a operador (afinar bot RAG)",
              "Se cerró sin que el ciudadano sintiera resolución (capacitar en cuándo cerrar)",
            ]}
          />
        </Section>

        {/* ---- 8. Auditoría ---- */}
        <Section title="8. Auditoría — rastrear acciones específicas">
          <p>
            Cada acción importante en MiPueblo queda registrada con quién la
            hizo y cuándo. Para investigar algo puntual:
          </p>
          <Ol>
            <li>
              Si es de un radicado: entra al radicado → tab{" "}
              <Kbd>Historial / Auditoría</Kbd> → ves todos los eventos
              (creación, asignación, traslados, respuestas, firmas)
            </li>
            <li>
              Si es de una conversación de chat: <Kbd>Cerradas</Kbd> → abrir →
              en el transcript verás mensajes de sistema con quién tomó, quién
              cerró, etc.
            </li>
            <li>
              Si necesitas auditoría más amplia (ej. "todo lo que hizo Juan
              ayer"), pídele al <code>admin_tenant</code> que te exporte el log
              global
            </li>
          </Ol>
          <p>
            <strong>Casos típicos de auditoría:</strong> reclamo de un
            ciudadano que dice no le respondieron, queja interna entre
            funcionarios sobre quién debía atender un caso, preparación de
            evidencia para una respuesta a un ente de control (Procuraduría,
            Personería).
          </p>
        </Section>

        {/* ---- 9. Capacitación ---- */}
        <Section title="9. Capacitar operadores nuevos (mini-onboarding)">
          <p>
            Cuando entra un operador nuevo, agenda una sesión de 60-90 min y
            cubre, en este orden:
          </p>
          <Checklist
            items={[
              <>
                <strong>Recorrido del panel:</strong> Panel, Mi bandeja, Chat
                ciudadano, Métricas
              </>,
              <>
                <strong>Lectura completa de la</strong>{" "}
                <Link to="/docs/operador">Guía del operador</Link>
              </>,
              <>
                <strong>Práctica en vivo:</strong> hazle tomar 2-3 chats reales
                contigo al lado. Revisa cada mensaje antes de enviar.
              </>,
              <>
                <strong>Responder 1 PQRSDF supervisado:</strong> guíalo en el
                editor, plantillas, IA-draft, firma
              </>,
              <>
                <strong>Plazos legales:</strong> repasa Ley 1755 y por qué los
                vencidos son críticos
              </>,
              <>
                <strong>Tono institucional:</strong> léele 5 respuestas tuyas
                ejemplares para que calibre estilo
              </>,
              <>
                <strong>Encuestas:</strong> muéstrale 2-3 buenas y 2-3 malas
                para que vea qué se evalúa
              </>,
              <>
                <strong>Casos especiales:</strong> qué hacer con denuncias de
                corrupción, datos sensibles, periodistas
              </>,
              <>
                <strong>A quién acudir:</strong> dudas operativas → tú; dudas
                legales → jurídica; problemas técnicos → admin_tenant /
                soporte MiPueblo
              </>,
            ]}
          />
          <div className="callout tip">
            💡 Los primeros 5 días del nuevo operador, revisa al final del día
            todos sus chats cerrados y PQRSDFs respondidos. Da feedback
            específico.
          </div>
        </Section>

        {/* ---- 10. Reportes ---- */}
        <Section title="10. Reportes a la dirección de la alcaldía">
          <p>
            El alcalde y el secretario general no van a entrar a MiPueblo; tú
            les traduces los datos. Estructura sugerida para informe mensual
            (1 página):
          </p>
          <Ol>
            <li>
              <strong>Volumen del mes:</strong> total PQRSDFs + total chats +
              comparativo con mes anterior
            </li>
            <li>
              <strong>Cumplimiento SLA:</strong> % respondidos en plazo (Ley
              1755). Si hubo vencidos, cuántos y por qué.
            </li>
            <li>
              <strong>Distribución por dependencia:</strong> qué áreas cargan
              más. Tabla con conteo.
            </li>
            <li>
              <strong>Top 5 temas más consultados:</strong> agrupando por
              asunto. Le dice al alcalde dónde está la presión ciudadana.
            </li>
            <li>
              <strong>Satisfacción ciudadana:</strong> NPS promedio del mes,
              variación, top 3 comentarios positivos y 3 negativos textuales.
            </li>
            <li>
              <strong>Productividad del equipo:</strong> tiempos promedio,
              casos por operador (sin nombres si va a junta directiva, con
              nombres si es interno).
            </li>
            <li>
              <strong>Riesgos y peticiones:</strong> casos delicados que
              requieren decisión política, necesidades de personal, mejoras de
              herramienta.
            </li>
          </Ol>
          <p>
            En <Kbd>Métricas e informes</Kbd> usa el botón <Kbd>Exportar</Kbd>{" "}
            (si está disponible en tu plan) para sacar CSV / PDF que puedas
            anexar al informe.
          </p>
          <div className="callout">
            <strong>Cadencia recomendada:</strong>
            <ul style={{ marginTop: 4 }}>
              <li>
                <strong>Diaria</strong>: chequeo del KPI Vencidos (5 min)
              </li>
              <li>
                <strong>Semanal</strong>: revisión de encuestas malas + reunión
                operativa de 30 min con el equipo
              </li>
              <li>
                <strong>Mensual</strong>: informe a la dirección + retro con
                operadores
              </li>
              <li>
                <strong>Trimestral</strong>: análisis de tendencias, propuesta
                de mejoras al admin_tenant
              </li>
            </ul>
          </div>
        </Section>
      </section>
    </Shell>
  );
}

/* ============ helpers de presentación ============ */

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        marginTop: 32,
        paddingTop: 24,
        borderTop: "1px solid var(--hairline)",
      }}
    >
      <h2 style={{ fontSize: 20, marginTop: 0, marginBottom: 14 }}>{title}</h2>
      <div style={{ lineHeight: 1.6, color: "var(--ink-2)", fontSize: 14.5 }}>
        {children}
      </div>
    </section>
  );
}

function Ol({ children }: { children: React.ReactNode }) {
  return (
    <ol style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 6 }}>
      {children}
    </ol>
  );
}

function Ul({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ paddingLeft: 22, margin: "8px 0", display: "grid", gap: 4 }}>
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}

function Checklist({ items }: { items: React.ReactNode[] }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "grid", gap: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 16,
              height: 16,
              border: "1.5px solid var(--ink-3)",
              borderRadius: 4,
              marginTop: 3,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        background: "var(--bone-warm)",
        border: "1px solid var(--hairline-strong)",
        borderRadius: 4,
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 12.5,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Table({
  head,
  rows,
}: {
  head?: string[];
  rows: (string | React.ReactNode)[][];
}) {
  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        overflow: "hidden",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        {head && (
          <thead>
            <tr style={{ background: "var(--bone-warm)" }}>
              {head.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    fontWeight: 500,
                    borderBottom: "1px solid var(--hairline)",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={{ borderTop: i === 0 && !head ? 0 : "1px solid var(--hairline)" }}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  style={{
                    padding: "10px 12px",
                    verticalAlign: "top",
                    fontWeight: j === 0 ? 600 : 400,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
